import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import Wrapper from '../../components/Wrapper';
import {
  Paper,
  Typography,
  Box,
  IconButton,
  Button,
  TextField,
  Alert,
  Tabs,
  Tab,
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SmsIcon from '@mui/icons-material/Sms';
import InfoIcon from '@mui/icons-material/Info';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DescriptionIcon from '@mui/icons-material/Description';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import TelegramIcon from '@mui/icons-material/Telegram';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentPasteGoIcon from '@mui/icons-material/ContentPasteGo';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import Api from '../../Api';
import { greenTheme } from '../../helpers/style';
import { smsProviding } from '../../translate';
import { toast } from 'react-toastify';
// import TimePicker from '../../components/TimePicker';

const { REACT_APP_SERVER } = process.env;
const fileInputId = 'sms-providing-file-input';

const primary = REACT_APP_SERVER === 'ATS' ? '#0b73b9' : greenTheme.primary;
const primaryHover = REACT_APP_SERVER === 'ATS' ? '#095a8f' : greenTheme.hover;
const telegramColor = '#0088cc';
const whatsappColor = '#25D366';

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    '& fieldset': { borderColor: 'divider', borderWidth: 1.5 },
    '&:hover fieldset': { borderColor: primary, borderWidth: 2 },
    '&.Mui-focused fieldset': { borderColor: primary, borderWidth: 2 },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: primary },
};

const sectionCardSx = {
  p: 2.5,
  borderRadius: 3,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
};

const btnOutlinedSx = {
  color: primary,
  borderColor: primary,
  borderRadius: 2,
  textTransform: 'none',
  fontWeight: 600,
  px: 2,
  '&:hover': {
    borderColor: primaryHover,
    backgroundColor: 'rgba(126,188,10,0.06)',
  },
};

const btnContainedSx = {
  backgroundColor: primary,
  borderRadius: 2,
  textTransform: 'none',
  fontWeight: 600,
  px: 3,
  py: 1.25,
  boxShadow: '0 2px 8px rgba(126,188,10,0.35)',
  '&:hover': {
    backgroundColor: primaryHover,
    boxShadow: '0 4px 12px rgba(126,188,10,0.4)',
  },
};

function getTemplateBodyText(template) {
  const components = Array.isArray(template?.components) ? template.components : [];
  const bodyComponent = components.find((c) => String(c?.type || '').toUpperCase() === 'BODY');

  if (typeof bodyComponent?.text === 'string') return bodyComponent.text;
  if (typeof bodyComponent?.text?.body === 'string') return bodyComponent.text.body;
  if (typeof template?.body === 'string') return template.body;

  return '';
}

function getTemplateHeaderText(template) {
  const components = Array.isArray(template?.components) ? template.components : [];
  const headerComponent = components.find((c) => String(c?.type || '').toUpperCase() === 'HEADER');

  if (typeof headerComponent?.text === 'string') return headerComponent.text;
  if (typeof headerComponent?.text?.header === 'string') return headerComponent.text.header;

  return '';
}

function getTemplateFooterText(template) {
  const components = Array.isArray(template?.components) ? template.components : [];
  const footerComponent = components.find((c) => String(c?.type || '').toUpperCase() === 'FOOTER');

  if (typeof footerComponent?.text === 'string') return footerComponent.text;
  if (typeof footerComponent?.text?.text === 'string') return footerComponent.text.text;

  return '';
}

function extractPlaceholderCount(text) {
  if (!text || typeof text !== 'string') return 0;
  const matches = [...text.matchAll(/{{\s*(\d+)\s*}}/g)];
  if (!matches.length) return 0;
  return Math.max(...matches.map((m) => Number(m[1])));
}

function normalizePhone(phone) {
  return String(phone || '').replace(/\D/g, '');
}

function replaceTemplatePreview(text, params = []) {
  return String(text || '').replace(/{{\s*(\d+)\s*}}/g, (_, index) => {
    const value = params[Number(index) - 1];
    return value?.trim() ? value : `{{${index}}}`;
  });
}

function detectPhoneField(row) {
  if (!row || typeof row !== 'object') return null;

  const keys = Object.keys(row);
  const lowerMap = Object.fromEntries(keys.map((k) => [k.toLowerCase().trim(), k]));

  const candidates = [
    'phone',
    'telephone',
    'mobile',
    'msisdn',
    'number',
    'tel',
    'phonenumber',
    'phone_number',
    'telefon',
    'հեռախոս',
    'номер',
    'телефон',
  ];

  for (const candidate of candidates) {
    if (lowerMap[candidate]) return lowerMap[candidate];
  }

  return keys[0] || null;
}

const TG_TEMPLATES_STORAGE_PREFIX = 'sms_providing_tg_templates_';

function loadTelegramTemplatesFromStorage() {
  try {
    const member = Cookies.get('member') || 'guest';
    const raw = localStorage.getItem(`${TG_TEMPLATES_STORAGE_PREFIX}${member}`);
    const parsed = JSON.parse(raw || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveTelegramTemplatesToStorage(list) {
  try {
    const member = Cookies.get('member') || 'guest';
    localStorage.setItem(`${TG_TEMPLATES_STORAGE_PREFIX}${member}`, JSON.stringify(list));
  } catch {
    /* ignore quota / private mode */
  }
}

function rowsToRecipients(rows) {
  if (!Array.isArray(rows)) return [];

  const recipients = [];

  for (const row of rows) {
    if (row == null) continue;

    if (typeof row === 'string' || typeof row === 'number') {
      const phone = normalizePhone(row);
      if (phone) recipients.push({ phone });
      continue;
    }

    if (Array.isArray(row)) {
      const phone = normalizePhone(row[0]);
      if (phone) recipients.push({ phone });
      continue;
    }

    if (typeof row === 'object') {
      const phoneField = detectPhoneField(row);
      const phone = normalizePhone(phoneField ? row[phoneField] : '');
      if (phone) recipients.push({ ...row, phone });
    }
  }

  return recipients.filter((r) => r.phone);
}

async function parseCsvFile(file) {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => resolve(rowsToRecipients(results.data || [])),
      error: reject,
    });
  });
}

async function parseExcelFile(file) {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const firstSheetName = workbook.SheetNames?.[0];
  if (!firstSheetName) return [];

  const worksheet = workbook.Sheets[firstSheetName];
  const rows = XLSX.utils.sheet_to_json(worksheet, {
    defval: '',
    raw: false,
  });

  return rowsToRecipients(rows);
}

export default function SmsProvidingCreate() {
  const navigate = useNavigate();
  const langIndex = +localStorage.getItem('atsLang') || 1;

  const [channel, setChannel] = useState('whatsapp');
  const [waPhoneNumbers, setWaPhoneNumbers] = useState([]);
  const [waPhoneLoading, setWaPhoneLoading] = useState(false);
  const [selectedWaPhoneId, setSelectedWaPhoneId] = useState(null);
  const [tgAccounts, setTgAccounts] = useState([]);
  const [tgAccountsLoading, setTgAccountsLoading] = useState(false);
  const [selectedTgAccountId, setSelectedTgAccountId] = useState(null);

  const [fileRecipients, setFileRecipients] = useState([]);
  const [message, setMessage] = useState('');
  // Scheduling UI is disabled until backend supports it.
  // const [sendAtDate, setSendAtDate] = useState('');
  // const [sendAtTime, setSendAtTime] = useState('');
  const [sending, setSending] = useState(false);

  const [metaTemplates, setMetaTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  const [createTemplateOpen, setCreateTemplateOpen] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    category: 'MARKETING',
    language: 'en_US',
    body: '',
    header: '',
    footer: '',
  });
  const [creatingTemplate, setCreatingTemplate] = useState(false);

  const [waSendMode, setWaSendMode] = useState('message');
  const [tgSendMode, setTgSendMode] = useState('message');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateParams, setTemplateParams] = useState([]);

  const [testPhone, setTestPhone] = useState('');
  const [testingTemplate, setTestingTemplate] = useState(false);

  const [telegramTemplates, setTelegramTemplates] = useState(loadTelegramTemplatesFromStorage);
  const [tgTemplateDialogOpen, setTgTemplateDialogOpen] = useState(false);
  const [tgTemplateDraft, setTgTemplateDraft] = useState({ name: '', body: '' });

  const fileInputRef = useRef(null);

  const selectedTemplateBody = useMemo(() => getTemplateBodyText(selectedTemplate), [selectedTemplate]);
  const selectedTemplateHeader = useMemo(() => getTemplateHeaderText(selectedTemplate), [selectedTemplate]);
  const selectedTemplateFooter = useMemo(() => getTemplateFooterText(selectedTemplate), [selectedTemplate]);
  const selectedTemplateParamCount = useMemo(
    () => extractPlaceholderCount(selectedTemplateBody),
    [selectedTemplateBody]
  );

  const templatePreviewText = useMemo(() => {
    const header = replaceTemplatePreview(selectedTemplateHeader, templateParams);
    const body = replaceTemplatePreview(selectedTemplateBody, templateParams);
    const footer = replaceTemplatePreview(selectedTemplateFooter, templateParams);
    return [header, body, footer].filter(Boolean).join('\n\n');
  }, [selectedTemplateHeader, selectedTemplateBody, selectedTemplateFooter, templateParams]);

  const handleFileUpload = useCallback(async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    e.target.value = '';

    try {
      const ext = file.name.toLowerCase().split('.').pop();
      let list = [];

      if (ext === 'csv') {
        list = await parseCsvFile(file);
      } else if (ext === 'xlsx' || ext === 'xls') {
        list = await parseExcelFile(file);
      } else {
        toast.warning('Unsupported file format. Use CSV, XLSX, or XLS.');
        return;
      }

      setFileRecipients(list);
      toast.success(`Loaded ${list.length} recipient(s) from file.`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to parse file');
    }
  }, []);

  const fetchWaPhoneNumbers = useCallback(async () => {
    try {
      setWaPhoneLoading(true);
      const res = await Api.whatsappGet();
      const data = res.data?.data;
      setWaPhoneNumbers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to get WhatsApp numbers:', err);
      setWaPhoneNumbers([]);
    } finally {
      setWaPhoneLoading(false);
    }
  }, []);

  useEffect(() => {
    if (channel === 'whatsapp') fetchWaPhoneNumbers();
  }, [channel, fetchWaPhoneNumbers]);

  const fetchTelegramAccounts = useCallback(async () => {
    try {
      setTgAccountsLoading(true);
      const res = await Api.telegramGet();
      const list = res?.data?.data || res?.data?.rows || [];
      setTgAccounts(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Failed to get Telegram accounts:', err);
      setTgAccounts([]);
    } finally {
      setTgAccountsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (channel === 'telegram') fetchTelegramAccounts();
  }, [channel, fetchTelegramAccounts]);

  useEffect(() => {
    if (waPhoneNumbers.length > 0 && selectedWaPhoneId == null) {
      setSelectedWaPhoneId(waPhoneNumbers[0].id);
    } else if (waPhoneNumbers.length === 0) {
      setSelectedWaPhoneId(null);
    }
  }, [waPhoneNumbers, selectedWaPhoneId]);

  useEffect(() => {
    if (tgAccounts.length > 0 && selectedTgAccountId == null) {
      setSelectedTgAccountId(tgAccounts[0].id);
    } else if (tgAccounts.length === 0) {
      setSelectedTgAccountId(null);
    }
  }, [tgAccounts, selectedTgAccountId]);

  const fetchMetaTemplates = useCallback(async () => {
    if (channel !== 'whatsapp' || !selectedWaPhoneId) return;

    try {
      setTemplatesLoading(true);
      const { data } = await Api.smsGetMetaTemplates(selectedWaPhoneId);
      const list = Array.isArray(data?.templates) ? data.templates : [];
      setMetaTemplates(list);

      if (list.length > 0) toast.success('Templates loaded.');
      else toast.info('No templates found.');
    } catch (err) {
      console.error(err);
      setMetaTemplates([]);
      toast.error(err?.response?.data?.message || 'Failed to fetch templates');
    } finally {
      setTemplatesLoading(false);
    }
  }, [channel, selectedWaPhoneId]);

  const handleCreateTemplate = useCallback(async () => {
    const { name, category, language, body } = templateForm;

    if (!name?.trim() || !category || !language || !body?.trim()) {
      toast.warning('Name, category, language and body are required.');
      return;
    }

    try {
      setCreatingTemplate(true);

      await Api.smsCreateMetaTemplate({
        name: name.trim(),
        category,
        language: language.trim(),
        body: body.trim(),
        header: templateForm.header?.trim() || undefined,
        footer: templateForm.footer?.trim() || undefined,
        wa_phone_id: selectedWaPhoneId || undefined,
      });

      toast.success('Template submitted. It may need approval in Meta Business Suite.');
      setCreateTemplateOpen(false);
      setTemplateForm({
        name: '',
        category: 'MARKETING',
        language: 'en_US',
        body: '',
        header: '',
        footer: '',
      });
      fetchMetaTemplates();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || 'Failed to create template');
    } finally {
      setCreatingTemplate(false);
    }
  }, [templateForm, fetchMetaTemplates, selectedWaPhoneId]);

  const handleSelectTemplate = useCallback((template) => {
    setSelectedTemplate(template);
    setTestPhone('');

    const bodyText = getTemplateBodyText(template);
    const count = extractPlaceholderCount(bodyText);
    setTemplateParams(Array.from({ length: count }, () => ''));
  }, []);

  const handleTemplateParamChange = useCallback((index, value) => {
    setTemplateParams((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  }, []);

  const handleSaveTelegramTemplate = useCallback(() => {
    const name = String(tgTemplateDraft.name || '').trim();
    const body = String(tgTemplateDraft.body || '').trim();
    if (!name || !body) {
      toast.warning(smsProviding.tg_name_body_required[langIndex]);
      return;
    }
    const id = Date.now();
    setTelegramTemplates((prev) => {
      const next = [...prev, { id, name, body }];
      saveTelegramTemplatesToStorage(next);
      return next;
    });
    setTgTemplateDialogOpen(false);
    setTgTemplateDraft({ name: '', body: '' });
    toast.success(smsProviding.tg_template_saved[langIndex]);
  }, [tgTemplateDraft, langIndex]);

  const handleDeleteTelegramTemplate = useCallback((templateId) => {
    setTelegramTemplates((prev) => {
      const next = prev.filter((t) => t.id !== templateId);
      saveTelegramTemplatesToStorage(next);
      return next;
    });
  }, []);

  const handleApplyTelegramTemplate = useCallback(
    (tpl) => {
      if (!tpl?.body) return;
      setMessage((prev) => {
        if (tgSendMode === 'template') {
          return tpl.body;
        }
        return prev && String(prev).trim() ? `${String(prev).trim()}\n\n${tpl.body}` : tpl.body;
      });
      toast.info(smsProviding.tg_template_applied[langIndex]);
    },
    [langIndex, tgSendMode]
  );

  const handleTestTemplate = useCallback(async () => {
    try {
      if (!selectedWaPhoneId) {
        toast.warning('Please select a WhatsApp number');
        return;
      }

      if (!selectedTemplate?.name) {
        toast.warning('Please select a template');
        return;
      }

      if (!testPhone.trim()) {
        toast.warning('Please enter a test phone number');
        return;
      }

      if (String(selectedTemplate?.status || '').toUpperCase() !== 'APPROVED') {
        toast.warning('Only APPROVED templates can be tested');
        return;
      }

      if (
        selectedTemplateParamCount > 0 &&
        templateParams.slice(0, selectedTemplateParamCount).some((x) => !String(x || '').trim())
      ) {
        toast.warning('Please fill all template parameters');
        return;
      }

      setTestingTemplate(true);

      const payload = {
        wa_phone_id: selectedWaPhoneId,
        to: normalizePhone(testPhone),
        template_name: selectedTemplate.name,
        language: selectedTemplate.language || 'en',
        params:
          selectedTemplateParamCount > 0
            ? templateParams.slice(0, selectedTemplateParamCount)
            : [],
      };

      const { data } = await Api.smsTestWhatsAppTemplate(payload);

      console.log('test template response', data);
      toast.success('Test template sent successfully');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || 'Failed to send test template');
    } finally {
      setTestingTemplate(false);
    }
  }, [
    selectedWaPhoneId,
    selectedTemplate,
    testPhone,
    selectedTemplateParamCount,
    templateParams,
  ]);

  const handleSend = async () => {
    const recipients = fileRecipients
      .map((r) => {
        if (!r || typeof r !== 'object') return null;
        const phone = normalizePhone(r.phone);
        if (!phone) return null;
        return { ...r, phone };
      })
      .filter(Boolean);
    const phones = recipients.map((r) => r.phone);

    if (!fileRecipients.length) {
      toast.warning('Upload a recipients file (CSV/Excel) first');
      return;
    }

    if (!phones.length) {
      toast.warning('No phone numbers in the uploaded file');
      return;
    }

    // Scheduling is not supported yet (send immediately).
    const isScheduled = false;
    const sendAtIso = '';

    try {
      setSending(true);

      if (channel === 'whatsapp' && waSendMode === 'template') {
        if (!selectedWaPhoneId) {
          toast.warning('Please select a WhatsApp number');
          return;
        }

        if (!selectedTemplate?.name) {
          toast.warning('Please select a template');
          return;
        }

        if (
          selectedTemplateParamCount > 0 &&
          templateParams.slice(0, selectedTemplateParamCount).some((x) => !String(x || '').trim())
        ) {
          toast.warning('Please fill all template parameters');
          return;
        }

        const items = recipients.map((r) => ({
          phone: r.phone,
          name: r.name ?? r.Name ?? r.full_name ?? r.fio ?? undefined,
          template_name: selectedTemplate.name,
          language: selectedTemplate.language || 'en_US',
          components:
            selectedTemplateParamCount > 0
              ? [
                  {
                    type: 'body',
                    parameters: templateParams
                      .slice(0, selectedTemplateParamCount)
                      .map((value) => ({
                        type: 'text',
                        text: String(value),
                      })),
                  },
                ]
              : undefined,
        }));

        const { data } = await Api.smsSendWhatsAppTemplates({
          wa_phone_id: selectedWaPhoneId,
          items,
          // send_at: undefined,
        });

        const sent = data?.sent ?? 0;
        const failed = data?.failed ?? 0;

        if (sent > 0) toast.success(`Template sent to ${sent} recipient(s).`);
        if (failed > 0) toast.error(`Template failed for ${failed} recipient(s).`);

        if (sent > 0) {
          navigate('/short_function/sms_providing', {
            state: {
              newBulkSend: {
                channel,
                status: 'sent',
                send_at: null,
                created_at: new Date().toISOString(),
                message: `Template: ${selectedTemplate.name}`,
                phone_count: phones.length,
              },
            },
          });
        }

        return;
      }

      const text = (message || '').trim();
      if (!text) {
        toast.warning('Please enter a message');
        return;
      }

      if (channel === 'telegram' && !selectedTgAccountId) {
        toast.warning(smsProviding.telegram_sender_required?.[langIndex] || 'Please select a Telegram account to send from.');
        return;
      }

      const { data } =
        channel === 'telegram'
          ? await Api.smsTelegramSend({
              telegram_account_id: selectedTgAccountId,
              message: text,
              recipients,
              phones,
            })
          : await Api.smsSend(
              [],
              text,
              recipients,
              undefined,
              channel,
              {}
            );

      const sent = data?.sent ?? 0;
      const noPhone = data?.noPhone ?? [];
      const failed = data?.failed ?? 0;

      if (sent > 0) toast.success(`Sent to ${sent} recipient(s).`);
      if (noPhone.length > 0) toast.warning(`${noPhone.length} recipient(s) have no phone and were skipped.`);
      if (failed > 0) toast.error(`Failed to send to ${failed} recipient(s).`);

      if (sent > 0) {
        setMessage('');
        navigate('/short_function/sms_providing', {
          state: {
            newBulkSend: {
              channel,
              status: 'sent',
              send_at: null,
              created_at: new Date().toISOString(),
              message: text.slice(0, 120),
              phone_count: phones.length,
            },
          },
        });
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const showFreeTextMessage =
    !(channel === 'whatsapp' && waSendMode === 'template') &&
    !(channel === 'telegram' && tgSendMode === 'template');
  const isTemplateMode = channel === 'whatsapp' && waSendMode === 'template';

  return (
    <Wrapper>
      <div className="tableParent">
        <Paper sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 3,
              borderBottom: '2px solid rgba(0,0,0,0.08)',
              pb: 2.5,
            }}
          >
            <IconButton
              size="small"
              sx={{ color: primary }}
              onClick={() => navigate('/short_function/sms_providing')}
              aria-label="Back"
            >
              <ArrowBackIosNewIcon sx={{ fontSize: 20, fontWeight: 700 }} />
            </IconButton>
            <SmsIcon sx={{ color: primary, fontSize: 28, mr: 0.5 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, m: 0 }}>
              {smsProviding.create_title[langIndex]}
            </Typography>
          </Box>

          <Alert
            icon={<InfoIcon />}
            severity="success"
            sx={
              REACT_APP_SERVER !== 'ATS'
                ? { mb: 3 }
                : {
                    mb: 3,
                    border: `1px solid ${primary}`,
                    backgroundColor: `${primary}10`,
                    '& .MuiAlert-icon': { color: primary },
                  }
            }
          >
            <Typography
              variant="body2"
              component="span"
              dangerouslySetInnerHTML={{ __html: smsProviding.alert_info[langIndex] }}
            />
          </Alert>

          <Paper sx={{ p: 3, border: `1px solid ${primary}` }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Tabs
                value={channel}
                onChange={(_, v) => {
                  setChannel(v);
                  if (v !== 'whatsapp') {
                    setWaSendMode('message');
                    setSelectedTemplate(null);
                    setTemplateParams([]);
                    setTestPhone('');
                  }
                  if (v !== 'telegram') {
                    setTgSendMode('message');
                  }
                }}
                sx={{
                  minHeight: 42,
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    color: 'text.secondary',
                  },
                  '& .MuiTab-root[value="telegram"].Mui-selected': { color: telegramColor },
                  '& .MuiTab-root[value="whatsapp"].Mui-selected': { color: whatsappColor },
                  '& .MuiTabs-indicator': {
                    height: 3,
                    backgroundColor: channel === 'telegram' ? telegramColor : whatsappColor,
                  },
                }}
              >
                <Tab label={smsProviding.telegram[langIndex]} value="telegram" />
                <Tab label={smsProviding.whatsapp[langIndex]} value="whatsapp" />
              </Tabs>

              {channel === 'telegram' && (
                <>
                  <Box sx={sectionCardSx}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: 'text.primary', mb: 1.5 }}>
                      {smsProviding.telegram_sender_label[langIndex]}
                    </Typography>
                    {tgAccountsLoading ? (
                      <Typography variant="body2" color="text.secondary">
                        {smsProviding.telegram_sender_loading[langIndex]}
                      </Typography>
                    ) : tgAccounts.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                        {tgAccounts.map((acc) => {
                          const isSelected = selectedTgAccountId === acc.id;
                          const label =
                            acc.phone ||
                            acc.tg_user_id ||
                            acc.id;
                          return (
                            <Chip
                              key={acc.id}
                              icon={<TelegramIcon sx={{ color: isSelected ? '#fff' : telegramColor, fontSize: 18 }} />}
                              label={label}
                              size="medium"
                              variant={isSelected ? 'filled' : 'outlined'}
                              onClick={() => setSelectedTgAccountId(acc.id)}
                              sx={{
                                borderColor: telegramColor,
                                color: isSelected ? '#fff' : telegramColor,
                                bgcolor: isSelected ? telegramColor : 'transparent',
                                cursor: 'pointer',
                                '& .MuiChip-icon': { color: isSelected ? '#fff' : telegramColor },
                                '&:hover': { bgcolor: isSelected ? '#0077b5' : 'rgba(0, 136, 204, 0.12)' },
                              }}
                            />
                          );
                        })}
                        <IconButton
                          size="small"
                          onClick={fetchTelegramAccounts}
                          sx={{ color: telegramColor }}
                          aria-label="Refresh"
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {smsProviding.telegram_sender_empty[langIndex]}
                      </Typography>
                    )}
                  </Box>

                  <Box sx={sectionCardSx}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: 'text.primary', mb: 1.5 }}>
                      {smsProviding.send_mode_label[langIndex]}
                    </Typography>
                    <Tabs
                      value={tgSendMode}
                      onChange={(_, v) => setTgSendMode(v)}
                      sx={{
                        minHeight: 40,
                        '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
                        '& .MuiTab-root.Mui-selected': { color: telegramColor },
                        '& .MuiTabs-indicator': { backgroundColor: telegramColor, height: 3 },
                      }}
                    >
                      <Tab value="message" label={smsProviding.mode_message[langIndex]} />
                      <Tab value="template" label={smsProviding.mode_template[langIndex]} />
                    </Tabs>
                  </Box>

                  {tgSendMode === 'template' && (
                    <Box sx={{ ...sectionCardSx, borderColor: `${telegramColor}44`, bgcolor: 'rgba(0, 136, 204, 0.04)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                        <TelegramIcon sx={{ color: telegramColor, fontSize: 26, mt: 0.25 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle1" fontWeight={700} sx={{ color: 'text.primary' }}>
                            {smsProviding.tg_templates_section[langIndex]}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                            {smsProviding.tg_templates_hint[langIndex]}
                          </Typography>
                        </Box>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => {
                            setTgTemplateDraft({ name: '', body: '' });
                            setTgTemplateDialogOpen(true);
                          }}
                          sx={{
                            ...btnOutlinedSx,
                            borderColor: telegramColor,
                            color: telegramColor,
                            flexShrink: 0,
                            '&:hover': { borderColor: telegramColor, bgcolor: 'rgba(0, 136, 204, 0.08)' },
                          }}
                        >
                          {smsProviding.tg_add_template[langIndex]}
                        </Button>
                      </Box>

                      {telegramTemplates.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ pl: 0.5 }}>
                          {smsProviding.tg_empty_templates[langIndex]}
                        </Typography>
                      ) : (
                        <List
                          dense
                          sx={{
                            bgcolor: 'background.paper',
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            maxHeight: 220,
                            overflow: 'auto',
                          }}
                        >
                          {telegramTemplates.map((tpl) => (
                            <ListItem
                              key={tpl.id}
                              secondaryAction={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Button
                                    size="small"
                                    startIcon={<ContentPasteGoIcon sx={{ fontSize: 18 }} />}
                                    onClick={() => handleApplyTelegramTemplate(tpl)}
                                    sx={{
                                      textTransform: 'none',
                                      fontWeight: 600,
                                      color: telegramColor,
                                      minWidth: 0,
                                    }}
                                  >
                                    {smsProviding.tg_use_template[langIndex]}
                                  </Button>
                                  <IconButton
                                    edge="end"
                                    size="small"
                                    aria-label={smsProviding.tg_delete_template[langIndex]}
                                    onClick={() => handleDeleteTelegramTemplate(tpl.id)}
                                    sx={{ color: 'text.secondary' }}
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              }
                              sx={{ pr: 18, alignItems: 'flex-start' }}
                            >
                              <ListItemText
                                primary={tpl.name}
                                primaryTypographyProps={{ fontWeight: 600, noWrap: true }}
                                secondary={tpl.body?.length > 100 ? `${String(tpl.body).slice(0, 100)}…` : tpl.body}
                                secondaryTypographyProps={{ sx: { whiteSpace: 'pre-wrap' } }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      )}

                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.75 }}>
                          {smsProviding.tg_outgoing_preview_label[langIndex]}
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fafafa', minHeight: 72 }}>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: message.trim() ? 'text.primary' : 'text.disabled' }}>
                            {message.trim() ? message : smsProviding.tg_outgoing_preview_empty[langIndex]}
                          </Typography>
                        </Paper>
                      </Box>
                    </Box>
                  )}
                </>
              )}

              {channel === 'whatsapp' && (
                <>
                  <Box sx={sectionCardSx}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: 'text.primary', mb: 1.5 }}>
                      {smsProviding.whatsapp_outgoing_numbers?.[langIndex] || 'WhatsApp outgoing numbers'}
                    </Typography>

                    {waPhoneLoading ? (
                      <Typography variant="body2" color="text.secondary">
                        {smsProviding.loading_templates?.[langIndex] || 'Loading...'}
                      </Typography>
                    ) : waPhoneNumbers.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {waPhoneNumbers.map((item) => {
                          const isSelected = selectedWaPhoneId === item.id;

                          return (
                            <Chip
                              key={item.id}
                              icon={<WhatsAppIcon sx={{ color: isSelected ? '#fff' : '#25D366', fontSize: 18 }} />}
                              label={item.wa_title || item.wa_phone_number || `ID ${item.wa_phone_id}`}
                              size="medium"
                              variant={isSelected ? 'filled' : 'outlined'}
                              onClick={() => setSelectedWaPhoneId(item.id)}
                              sx={{
                                borderColor: '#25D366',
                                color: isSelected ? '#fff' : '#25D366',
                                bgcolor: isSelected ? '#25D366' : 'transparent',
                                cursor: 'pointer',
                                '& .MuiChip-icon': {
                                  color: isSelected ? '#fff' : '#25D366',
                                },
                                '&:hover': {
                                  bgcolor: isSelected ? '#1da851' : '#25D36622',
                                },
                              }}
                            />
                          );
                        })}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {smsProviding.whatsapp_no_numbers?.[langIndex] || 'No WhatsApp numbers connected'}
                      </Typography>
                    )}
                  </Box>

                  <Box sx={sectionCardSx}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: 'text.primary', mb: 1.5 }}>
                      {smsProviding.send_mode_label[langIndex]}
                    </Typography>
                    <Tabs
                      value={waSendMode}
                      onChange={(_, v) => setWaSendMode(v)}
                      sx={{ minHeight: 40, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}
                    >
                      <Tab value="message" label={smsProviding.mode_message[langIndex]} />
                      <Tab value="template" label={smsProviding.mode_template[langIndex]} />
                    </Tabs>
                  </Box>
                </>
              )}

              <Box sx={sectionCardSx}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: 'text.primary', mb: 1.5 }}>
                  {smsProviding.recipients[langIndex]}
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                  <input
                    id={fileInputId}
                    type="file"
                    ref={fileInputRef}
                    accept=".xlsx,.xls,.csv"
                    style={{ display: 'none' }}
                    onChange={handleFileUpload}
                  />

                  <Button
                    variant="outlined"
                    size="medium"
                    startIcon={<UploadFileIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    sx={btnOutlinedSx}
                  >
                    {smsProviding.upload_csv[langIndex]}
                  </Button>

                  <Button
                    component="a"
                    href="/demo_recipients.csv"
                    download="demo_recipients.csv"
                    variant="outlined"
                    size="medium"
                    startIcon={<DownloadIcon />}
                    sx={{ ...btnOutlinedSx, color: 'text.secondary', borderColor: 'divider' }}
                  >
                    {smsProviding.demo_recipients[langIndex]}
                  </Button>

                  {fileRecipients.length > 0 && (
                    <Chip
                      label={smsProviding.recipients_count[langIndex].replace('{n}', fileRecipients.length)}
                      size="medium"
                      onDelete={() => setFileRecipients([])}
                      sx={{
                        bgcolor: `${primary}18`,
                        color: primary,
                        fontWeight: 600,
                        '& .MuiChip-deleteIcon': { color: primary },
                      }}
                    />
                  )}
                </Box>
              </Box>

              {showFreeTextMessage && (
                <Box sx={sectionCardSx}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ color: 'text.primary', mb: 1.5 }}>
                    {smsProviding.message[langIndex]}
                  </Typography>
                  <TextField
                    placeholder={smsProviding.message_placeholder[langIndex]}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    multiline
                    minRows={4}
                    maxRows={8}
                    fullWidth
                    variant="outlined"
                    size="small"
                    sx={inputSx}
                  />
                </Box>
              )}

              {isTemplateMode && (
                <>
                  <Box sx={{ ...sectionCardSx, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: 'text.primary', mb: 1.5 }}>
                      {smsProviding.templates_meta?.[langIndex] || 'Meta templates'}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: metaTemplates.length > 0 ? 2 : 0 }}>
                      <Button
                        variant="outlined"
                        size="medium"
                        startIcon={<RefreshIcon />}
                        disabled={templatesLoading || !selectedWaPhoneId}
                        onClick={fetchMetaTemplates}
                        sx={btnOutlinedSx}
                      >
                        {templatesLoading
                          ? smsProviding.loading_templates?.[langIndex] || 'Loading...'
                          : smsProviding.fetch_templates?.[langIndex] || 'Fetch templates'}
                      </Button>

                      <Button
                        variant="outlined"
                        size="medium"
                        startIcon={<AddIcon />}
                        disabled={!selectedWaPhoneId}
                        onClick={() => setCreateTemplateOpen(true)}
                        sx={btnOutlinedSx}
                      >
                        {smsProviding.create_template?.[langIndex] || 'Create template'}
                      </Button>
                    </Box>

                    {metaTemplates.length > 0 && (
                      <List
                        dense
                        sx={{
                          bgcolor: 'background.paper',
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          maxHeight: 260,
                          overflow: 'auto',
                        }}
                      >
                        {metaTemplates.map((t, i) => {
                          const isSelected =
                            (selectedTemplate?.id && t?.id && selectedTemplate.id === t.id) ||
                            (!selectedTemplate?.id &&
                              selectedTemplate?.name &&
                              t?.name &&
                              selectedTemplate.name === t.name);

                          const previewBody = getTemplateBodyText(t);

                          return (
                            <ListItem key={t.id || t.name || i} disablePadding>
                              <ListItemButton
                                selected={!!isSelected}
                                onClick={() => handleSelectTemplate(t)}
                                sx={{
                                  borderRadius: 1,
                                  '&:hover': { bgcolor: `${primary}12` },
                                  '&.Mui-selected': { bgcolor: `${primary}18` },
                                  '&.Mui-selected:hover': { bgcolor: `${primary}22` },
                                }}
                              >
                                <ListItemText
                                  primary={t.name}
                                  primaryTypographyProps={{ fontWeight: 600 }}
                                  secondary={[t.status, t.language, previewBody?.slice(0, 80)].filter(Boolean).join(' · ')}
                                />
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </List>
                    )}
                  </Box>

                  {selectedTemplate && (
                    <>
                      <Box sx={sectionCardSx}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ color: 'text.primary', mb: 1.5 }}>
                          Selected template
                        </Typography>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                          <Chip label={`Name: ${selectedTemplate.name || '-'}`} />
                          <Chip label={`Language: ${selectedTemplate.language || '-'}`} />
                          <Chip label={`Status: ${selectedTemplate.status || '-'}`} />
                        </Box>

                        {!!selectedTemplateHeader && (
                          <>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                              Header
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1.5, whiteSpace: 'pre-wrap' }}>
                              {selectedTemplateHeader}
                            </Typography>
                          </>
                        )}

                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                          Body
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1.5, whiteSpace: 'pre-wrap' }}>
                          {selectedTemplateBody || '-'}
                        </Typography>

                        {!!selectedTemplateFooter && (
                          <>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                              Footer
                            </Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {selectedTemplateFooter}
                            </Typography>
                          </>
                        )}
                      </Box>

                      {selectedTemplateParamCount > 0 && (
                        <Box sx={sectionCardSx}>
                          <Typography variant="subtitle1" fontWeight={700} sx={{ color: 'text.primary', mb: 1.5 }}>
                            Template parameters
                          </Typography>

                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {Array.from({ length: selectedTemplateParamCount }).map((_, index) => (
                              <TextField
                                key={index}
                                label={`Parameter ${index + 1}`}
                                value={templateParams[index] || ''}
                                onChange={(e) => handleTemplateParamChange(index, e.target.value)}
                                fullWidth
                                size="small"
                                sx={inputSx}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}

                      <Box sx={sectionCardSx}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ color: 'text.primary', mb: 1.5 }}>
                          Preview
                        </Typography>
                        <Paper
                          variant="outlined"
                          sx={{ p: 2, borderRadius: 2, bgcolor: '#fafafa', whiteSpace: 'pre-wrap' }}
                        >
                          <Typography variant="body2">{templatePreviewText || 'No preview available'}</Typography>
                        </Paper>
                      </Box>

                      <Box sx={sectionCardSx}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ color: 'text.primary', mb: 1.5 }}>
                          Test template
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                          <TextField
                            label="Test phone"
                            value={testPhone}
                            onChange={(e) => setTestPhone(e.target.value)}
                            placeholder="37499111222"
                            fullWidth
                            size="small"
                            sx={{ ...inputSx, flex: 1, minWidth: 220 }}
                          />

                          <Button
                            variant="outlined"
                            onClick={handleTestTemplate}
                            disabled={!selectedWaPhoneId || !selectedTemplate || testingTemplate}
                            sx={btnOutlinedSx}
                          >
                            {testingTemplate ? 'Sending test...' : 'Send test'}
                          </Button>
                        </Box>
                      </Box>
                    </>
                  )}
                </>
              )}

              {/* Scheduling UI disabled until backend is ready */}

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 2,
                  pt: 1,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => navigate('/short_function/sms_providing')}
                  sx={btnOutlinedSx}
                >
                  {smsProviding.back[langIndex]}
                </Button>

                <Button
                  variant="contained"
                  disabled={
                    sending ||
                    fileRecipients.length === 0 ||
                    (isTemplateMode ? !selectedTemplate : !message.trim())
                  }
                  onClick={handleSend}
                  sx={btnContainedSx}
                >
                  {sending
                    ? smsProviding.sending?.[langIndex] || 'Sending...'
                    : (smsProviding.send_count?.[langIndex] || 'Send ({n})').replace('{n}', fileRecipients.length)}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Paper>
      </div>

      <Dialog
        open={createTemplateOpen}
        onClose={() => !creatingTemplate && setCreateTemplateOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, borderBottom: '1px solid', borderColor: 'divider', pb: 2 }}>
          {smsProviding.create_meta_template?.[langIndex] || 'Create Meta template'}
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label={smsProviding.template_name?.[langIndex] || 'Template name'}
              value={templateForm.name}
              onChange={(e) => setTemplateForm((p) => ({ ...p, name: e.target.value }))}
              placeholder={smsProviding.template_name_placeholder?.[langIndex] || 'appointment_reminder'}
              fullWidth
              size="small"
              sx={inputSx}
            />

            <TextField
              select
              SelectProps={{ native: true }}
              label={smsProviding.category?.[langIndex] || 'Category'}
              value={templateForm.category}
              onChange={(e) => setTemplateForm((p) => ({ ...p, category: e.target.value }))}
              fullWidth
              size="small"
              sx={inputSx}
            >
              <option value="MARKETING">MARKETING</option>
              <option value="UTILITY">UTILITY</option>
              <option value="AUTHENTICATION">AUTHENTICATION</option>
            </TextField>

            <TextField
              label={smsProviding.language?.[langIndex] || 'Language'}
              value={templateForm.language}
              onChange={(e) => setTemplateForm((p) => ({ ...p, language: e.target.value }))}
              placeholder="en_US"
              fullWidth
              size="small"
              sx={inputSx}
            />

            <TextField
              label={smsProviding.body_required?.[langIndex] || 'Body (required)'}
              value={templateForm.body}
              onChange={(e) => setTemplateForm((p) => ({ ...p, body: e.target.value }))}
              multiline
              rows={3}
              fullWidth
              size="small"
              sx={inputSx}
            />

            <TextField
              label={smsProviding.header_optional?.[langIndex] || 'Header (optional)'}
              value={templateForm.header}
              onChange={(e) => setTemplateForm((p) => ({ ...p, header: e.target.value }))}
              fullWidth
              size="small"
              sx={inputSx}
            />

            <TextField
              label={smsProviding.footer_optional?.[langIndex] || 'Footer (optional)'}
              value={templateForm.footer}
              onChange={(e) => setTemplateForm((p) => ({ ...p, footer: e.target.value }))}
              fullWidth
              size="small"
              sx={inputSx}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            onClick={() => setCreateTemplateOpen(false)}
            disabled={creatingTemplate}
            sx={btnOutlinedSx}
          >
            {smsProviding.cancel?.[langIndex] || 'Cancel'}
          </Button>

          <Button
            variant="contained"
            onClick={handleCreateTemplate}
            disabled={creatingTemplate}
            sx={btnContainedSx}
          >
            {creatingTemplate
              ? smsProviding.submitting?.[langIndex] || 'Submitting...'
              : smsProviding.submit?.[langIndex] || 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={tgTemplateDialogOpen}
        onClose={() => setTgTemplateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            borderBottom: '1px solid',
            borderColor: 'divider',
            pb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <TelegramIcon sx={{ color: telegramColor }} />
          {smsProviding.tg_template_dialog_title[langIndex]}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label={smsProviding.tg_template_name_label[langIndex]}
              value={tgTemplateDraft.name}
              onChange={(e) => setTgTemplateDraft((p) => ({ ...p, name: e.target.value }))}
              fullWidth
              size="small"
              sx={inputSx}
            />
            <TextField
              label={smsProviding.tg_template_body_label[langIndex]}
              value={tgTemplateDraft.body}
              onChange={(e) => setTgTemplateDraft((p) => ({ ...p, body: e.target.value }))}
              multiline
              minRows={4}
              maxRows={10}
              fullWidth
              size="small"
              sx={inputSx}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={() => setTgTemplateDialogOpen(false)} sx={btnOutlinedSx}>
            {smsProviding.cancel[langIndex]}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveTelegramTemplate}
            sx={{ ...btnContainedSx, backgroundColor: telegramColor, '&:hover': { backgroundColor: '#0077b5' } }}
          >
            {smsProviding.tg_save_template[langIndex]}
          </Button>
        </DialogActions>
      </Dialog>
    </Wrapper>
  );
}